"use client"

import React, { useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { submitCatalogueOrder } from "@/actions/catalogueOrderActions"
import { verifyReCaptcha } from "@/actions/reCaptchaActions"
import { useTranslations } from "next-intl"
import { renderToStaticMarkup } from "react-dom/server"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { SubmitHandler, useForm } from "react-hook-form"
import { MARK_BOLD, MARK_LINK, NODE_PARAGRAPH, render, StoryblokRichtext } from "storyblok-rich-text-react-renderer"
import { z } from "zod"

import { sendContactEmail } from "@/lib/email/smtp-client"
import { resolveStoryblokStoryHref } from "@/lib/link-utils/storyblok-link-resolver"
import { isProdMode } from "@/lib/nodeHelper"
import { zodResolver } from "@/lib/polyfill/zodResolver"
import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Heading,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/components"
import { renderRichTextChildrenOrBreak } from "@/components/ui/helpers/richtextRenderer"
import { cn } from "@/components/ui/helpers/utils"
import EmailTemplateContact from "@/components/email-templates/EmailTemplateContact"
import { AssetStoryblok, MultilinkStoryblok, RichtextStoryblok } from "@/components/storyblok/component-types"

interface UbCatalogueOrderFormClientProps {
  title?: string
  description?: string
  legalConsentNotification: RichtextStoryblok
  captchaHint: RichtextStoryblok
  labelHintMandatoryField?: string
  // New: allow SB-managed redirect links (story or url) like Reclamation form
  httpRedirect: MultilinkStoryblok
  httpRedirectError: MultilinkStoryblok
  sbLocale?: string
  // Optional SB-driven email config
  fromEmail?: string
  toEmail?: string
  emailSubject?: string
  emailImage?: AssetStoryblok
  emailHtmlBody?: RichtextStoryblok
  emailTextBody?: string
  emailFooterLeft?: RichtextStoryblok
  emailFooterRight?: RichtextStoryblok
  emailFooterBottom?: RichtextStoryblok
}

const LEGAL_CONSENT_VALUE =
  'Mit Absenden des Formulars erkennen Sie unsere <a href="https://www.udobaer.de/info/de-de/datenschutz" target="_blank">Datenschutzbestimmungen</a> an..'

export default function UbCatalogueOrderFormClient({
  title,
  description,
  legalConsentNotification,
  captchaHint,
  labelHintMandatoryField,
  httpRedirect,
  httpRedirectError,
  sbLocale,
  fromEmail,
  toEmail,
  emailSubject,
  emailImage,
  emailHtmlBody,
  emailTextBody,
  emailFooterLeft,
  emailFooterRight,
  emailFooterBottom,
}: UbCatalogueOrderFormClientProps) {
  const t = useTranslations("storefront")
  const router = useRouter()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [sending, setSending] = useState(false)
  const DEV_SEND_EMAILS = isProdMode()
  const DEV_SEND_ONLY_EXTERNAL_EMAIL = !isProdMode()

  // Resolve redirect links similar to UbReclamationFormClient
  const resolveLink = (link: MultilinkStoryblok | string | undefined): string => {
    if (!link) return ""
    if (typeof link === "string") return link
    const l = link as MultilinkStoryblok
    if (l.linktype === "story" && l.cached_url) {
      // @ts-ignore - story may exist only at runtime
      return resolveStoryblokStoryHref(l.cached_url, sbLocale, (l as any).story)
    }
    if (l.linktype === "url" && (l as any).url) {
      return (l as any).url as string
    }
    return ""
  }
  const successRedirectLink = resolveLink(httpRedirect)
  const errorRedirectLink = resolveLink(httpRedirectError)

  const formSchema = z.object({
    CatalogOrder_Title: z.enum(["salutation.ms", "salutation.mr", "salutation.divers"], {
      error: (issue) => (issue.input === undefined ? undefined : undefined),
    }),
    CatalogOrder_Name: z
      .string()
      .min(2, t("forms.catalogue_order_form.error_name"))
      .max(50, t("forms.catalogue_order_form.error_max")),
    CatalogOrder_Company: z
      .string()
      .min(2, t("forms.catalogue_order_form.error_company"))
      .max(50, t("forms.catalogue_order_form.error_max")),
    CatalogOrder_Phone: z
      .string()
      .min(2, t("forms.catalogue_order_form.error_phone"))
      .max(50, t("forms.catalogue_order_form.error_max")),
    CatalogOrder_Email: z.email(t("forms.catalogue_order_form.error_email")),
    CatalogOrder_Street: z
      .string()
      .min(2, t("forms.catalogue_order_form.error_street"))
      .max(50, t("forms.catalogue_order_form.error_max")),
    CatalogOrder_ZIP: z
      .string()
      .regex(/^(?:\d{5}|A-\d{4}|CH-\d{4}|\d{4}\s?[A-Z]{2}|\d{4})$/, t("forms.catalogue_order_form.error_zip")),
    CatalogOrder_City: z
      .string()
      .min(2, t("forms.catalogue_order_form.error_city"))
      .max(50, t("forms.catalogue_order_form.error_max")),
    CatalogOrder_Country: z
      .string()
      .min(2, t("forms.catalogue_order_form.error_country"))
      .max(50, t("forms.catalogue_order_form.error_max")),
    acceptTerms: z.literal<boolean>(true, {
      error: () => t("forms.catalogue_order_form.error_terms"),
    }),
  })

  type FormSchemaType = z.infer<typeof formSchema>

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      CatalogOrder_Title: undefined,
      CatalogOrder_Name: "",
      CatalogOrder_Company: "",
      CatalogOrder_Phone: "",
      CatalogOrder_Email: "",
      CatalogOrder_Street: "",
      CatalogOrder_ZIP: "",
      CatalogOrder_City: "",
      CatalogOrder_Country: "",
      acceptTerms: false,
    },
  })

  const redirect = (succeeded: boolean) => {
    try {
      const target = succeeded ? successRedirectLink : errorRedirectLink
      target ? router.push(target) : router.back()
    } catch (error) {
      console.log(`UbCatalogueOrderFormClient: error on redirecting: ${error}`)
    }
  }

  // Email helpers (similar to Reclamation form)
  const sanitizeField = (text: string): string => {
    return text.replaceAll("\r\n", "\\n").replaceAll("\n", "\\n").replaceAll('"', '\\"')
  }

  const replacePlaceholder = (text: string, value: FormSchemaType): string => {
    return text
      .replaceAll("${Title}", sanitizeField(t(value.CatalogOrder_Title ?? "")))
      .replaceAll("${Name}", sanitizeField(value.CatalogOrder_Name))
      .replaceAll("${Company}", sanitizeField(value.CatalogOrder_Company))
      .replaceAll("${Phone}", sanitizeField(value.CatalogOrder_Phone))
      .replaceAll("${Email}", sanitizeField(value.CatalogOrder_Email))
      .replaceAll("${Street}", sanitizeField(value.CatalogOrder_Street))
      .replaceAll("${ZIP}", sanitizeField(value.CatalogOrder_ZIP))
      .replaceAll("${City}", sanitizeField(value.CatalogOrder_City))
      .replaceAll("${Country}", sanitizeField(value.CatalogOrder_Country))
  }

  const getEmailTextBody = (value: FormSchemaType) => {
    return replacePlaceholder(emailTextBody ?? "", value)
  }

  const getEmailHtmlBody = (value: FormSchemaType) => {
    if (!emailHtmlBody) return ""
    const richtextReplaced = replacePlaceholder(JSON.stringify(emailHtmlBody), value)
    const body = JSON.parse(richtextReplaced)
    return renderToStaticMarkup(
      <EmailTemplateContact
        sbLocale={sbLocale ?? "de-DE"}
        body={body}
        image={emailImage}
        footerLeft={emailFooterLeft as RichtextStoryblok}
        footerRight={emailFooterRight as RichtextStoryblok}
        footerBottom={emailFooterBottom as RichtextStoryblok}
      />
    )
  }

  const sendEmails = (value: FormSchemaType) => {
    const textBody = getEmailTextBody(value)
    const htmlBody = getEmailHtmlBody(value)

    const externalEmail = () =>
      sendContactEmail({
        from: fromEmail as string,
        to: value.CatalogOrder_Email,
        subject: emailSubject as string,
        text: textBody,
        html: htmlBody,
      })

    const internalEmail = () =>
      sendContactEmail({
        from: fromEmail as string,
        to: toEmail as string,
        subject: emailSubject as string,
        text: textBody,
        html: htmlBody,
      })

    if (DEV_SEND_EMAILS && fromEmail && toEmail && emailSubject) {
      Promise.all([externalEmail(), internalEmail()])
        .then(() => redirect(true))
        .catch((error) => {
          console.log(`UbCatalogueOrderFormClient: error sending emails: ${error}`)
          redirect(false)
        })
    } else if (DEV_SEND_ONLY_EXTERNAL_EMAIL) {
      externalEmail()
        .then(() => redirect(true))
        .catch((error) => {
          console.log(`UbReclamationFormClient: error sending external email: ${error}`)
          redirect(false)
        })
    } else {
      console.log(
        "UbCatalogueOrderFormClient: email delivery disabled or email config missing; falling back to legacy submit"
      )
      redirect(false)
    }
  }

  const onSubmit: SubmitHandler<FormSchemaType> = (data, event) => {
    event && event.preventDefault()
    const action = "CatalogOrder"
    setSending(true)

    if (executeRecaptcha) {
      executeRecaptcha(action)
        .then((token) => {
          verifyReCaptcha(token, action, "Katalog Bestellung")
            .then((result) => {
              if (result) {
                result ? sendEmails(data) : redirect(false)
              } else {
                redirect(false)
              }
            })
            .catch((error) => {
              console.log(`UbCatalogueOrderFormClient: error verifying reCaptcha: ${error.message}`)
              redirect(false)
            })
        })
        .catch((error) => {
          console.log(`UbCatalogueOrderFormClient: error executing recaptcha: ${error.message}`)
          redirect(false)
        })
    } else {
      console.log("UbCatalogueOrderFormClient: execute recaptcha not available yet")
      redirect(false)
    }
  }

  const renderedRichText = (richtext: StoryblokRichtext | unknown) => {
    return render(richtext, {
      markResolvers: {
        [MARK_BOLD]: (children) => <span className={"font-bold"}>{children}</span>,
        [MARK_LINK]: (children, { linktype, href, target }) => {
          if (linktype === "story" && href) {
            return (
              <Link prefetch={false} className={"text-primary"} href={resolveStoryblokStoryHref(href)} target={target}>
                {children}
              </Link>
            )
          }
          if (linktype === "url" && href) {
            return (
              <a
                className={"text-primary"}
                href={href}
                target={target}
                rel={target === "_blank" ? "noopener noreferrer" : undefined}
              >
                {children}
              </a>
            )
          }
          return (
            <a className={"text-primary"} href={"#"}>
              {children}
            </a>
          )
        },
      },
      nodeResolvers: {
        [NODE_PARAGRAPH]: (children) => {
          return <>{renderRichTextChildrenOrBreak(children)}</>
        },
      },
    })
  }

  return (
    <>
      {title && (
        <Heading element={"h2"} marginBottom={"6"}>
          {title}
        </Heading>
      )}
      <div className={"gap-6 lg:flex"}>
        <p className={"lg:w-1/4"}>{description}</p>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className={cn("lg:w-3/4", {
              "pointer-events-none cursor-wait opacity-30": sending,
            })}
          >
            <fieldset className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
              <FormField
                control={form.control}
                name="CatalogOrder_Title"
                render={({ field }) => (
                  <FormItem className={"lg:col-span-4"}>
                    <FormLabel className={"font-bold"}>
                      {t("forms.catalogue_order_form.label_title")}
                      <span className="ml-1 text-red-500">*</span>
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t("forms.catalogue_order_form.select_title")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="salutation.ms">Frau</SelectItem>
                        <SelectItem value="salutation.mr">Herr</SelectItem>
                        <SelectItem value="salutation.divers">Divers</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="CatalogOrder_Name"
                render={({ field }) => (
                  <FormItem className={"lg:col-span-4"}>
                    <FormLabel className={"font-bold"}>
                      {t("forms.catalogue_order_form.label_name")}
                      <span className="ml-1 text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="CatalogOrder_Company"
                render={({ field }) => (
                  <FormItem className={"lg:col-span-4"}>
                    <FormLabel className={"font-bold"}>
                      {t("forms.catalogue_order_form.label_company")}
                      <span className="ml-1 text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="CatalogOrder_Phone"
                render={({ field }) => (
                  <FormItem className={"lg:col-span-6"}>
                    <FormLabel className={"font-bold"}>
                      {t("forms.catalogue_order_form.label_phone")}
                      <span className="ml-1 text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input type={"tel"} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="CatalogOrder_Email"
                render={({ field }) => (
                  <FormItem className={"lg:col-span-6"}>
                    <FormLabel className={"font-bold"}>
                      {t("forms.catalogue_order_form.label_email")}
                      <span className="ml-1 text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input type={"email"} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="CatalogOrder_Street"
                render={({ field }) => (
                  <FormItem className={"lg:col-span-3"}>
                    <FormLabel className={"font-bold"}>
                      {t("forms.catalogue_order_form.label_street")}
                      <span className="ml-1 text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="CatalogOrder_ZIP"
                render={({ field }) => (
                  <FormItem className={"lg:col-span-3"}>
                    <FormLabel className={"font-bold"}>
                      {t("forms.catalogue_order_form.label_zip")}
                      <span className="ml-1 text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage className="w-[18rem] max-w-[calc(100vw-4rem)]" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="CatalogOrder_City"
                render={({ field }) => (
                  <FormItem className={"lg:col-span-3"}>
                    <FormLabel className={"font-bold"}>
                      {t("forms.catalogue_order_form.label_city")}
                      <span className="ml-1 text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="CatalogOrder_Country"
                render={({ field }) => (
                  <FormItem className={"lg:col-span-3"}>
                    <FormLabel className={"font-bold"}>
                      {t("forms.catalogue_order_form.label_country")}
                      <span className="ml-1 text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="acceptTerms"
                render={({ field }) => (
                  <FormItem className={"lg:col-span-12"}>
                    <FormControl>
                      <Checkbox className={"mr-2"} checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className={"text-base text-black"}>
                      {renderedRichText(legalConsentNotification)}
                    </FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </fieldset>
            <div className={"flex w-full flex-row items-end justify-end"}>
              <span className="ml-1 text-red-500">*</span>
              <span className={"text-sm"}>{labelHintMandatoryField}</span>
            </div>

            <Button type="submit" size={"sm"} className={"mb-6"} disabled={sending}>
              {t("forms.catalogue_order_form.send")}
            </Button>
            <p className={"text-sm"}>{renderedRichText(captchaHint)}</p>
          </form>
        </Form>
      </div>
    </>
  )
}
